import { DestroyRef, Directive, effect, input, signal, TemplateRef, ViewContainerRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { PermissionAction, PermissionResource } from '@coin/shared/core/util-enums';
import { UserState } from './user.state';
import { PermissionsService } from './permissions.service';

interface Permissions {
  allowedResources?: PermissionResource[];
  allowedActions?: PermissionAction[];
  directAllowAccess?: boolean;
}

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[permissions]',
  standalone: true
})
export class PermissionsDirective {
  private store = inject(Store);
  private destroyRef = inject(DestroyRef);
  private permissionsService = inject(PermissionsService);

  permissions = input<Permissions>(undefined);

  private hasPermission = signal(false);

  constructor() {
    const templateRef = inject<TemplateRef<unknown>>(TemplateRef);
    const viewContainer = inject(ViewContainerRef);

    effect(() => {
      if (this.hasPermission()) {
        if (!viewContainer.length) {
          viewContainer.createEmbeddedView(templateRef);
        }
      } else {
        viewContainer.clear();
      }
    });

    effect(() => this.updateVisibilityBasedOnPermissions(this.permissions()));
  }

  private updateVisibilityBasedOnPermissions(requiredPermissions: Permissions): void {
    this.store
      .select(UserState.allPermissions)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(userPermissions => {
        if (requiredPermissions.directAllowAccess) {
          this.hasPermission.set(true);
        } else if (userPermissions && requiredPermissions.allowedResources?.length) {
          const allowedActions = requiredPermissions.allowedActions || [PermissionAction.All, PermissionAction.Read];
          this.hasPermission.set(
            this.permissionsService.hasPermissionsAndActions({
              resources: requiredPermissions.allowedResources,
              actions: allowedActions
            })
          );
        }
      });
  }
}
